import React, { useState, useEffect } from 'react';
import './UserDashboard.css';
import ChatBubble from './components/chat/ChatBubble';
import UrbanIA from './components/UrbanIA/UrbanIA';
import UrbanIADisclaimer from './components/UrbanIA/UrbanIADisclaimer';
import AnnouncementPopup from './components/AnnouncementPopup';
import {
    Row, Col, Card, Table, Button, ProgressBar, Alert, Badge, Spinner
} from 'react-bootstrap';
import {
  FaHome,
  FaFileInvoiceDollar,
  FaBell,
  FaUser,
  FaFileInvoice,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import UserNotices from './avisos/UserNotices';
import UserProfile from './profile/UserProfile.js'; 
import UserFacturas from './facturas/UserFacturas'; 
import UserDashboardInicio from './Inicios/UserDashboardInicio';
import PagosUsers from './users/pagos/PagosUsers';
import UserReservas from './reservas/UserReservas';
import { API_ENDPOINTS } from './config/api';

// ===================================================================
// FUNCIONES AUXILIARES
// ===================================================================

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '$0.00';
    const numAmount = Number(amount);
    return `$${numAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (dateString.length === 10 && dateString.includes('-')) {
           const parts = dateString.split('-');
           const utcDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
           if (isNaN(utcDate.getTime())) return 'Fecha inválida';
           return utcDate.toLocaleDateString('es-EC', { 
               year: 'numeric', 
               month: 'long', 
               day: 'numeric', 
               timeZone: 'UTC' 
           });
        }
        if (isNaN(date.getTime())) return 'Fecha inválida';
        return date.toLocaleDateString('es-EC', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
    } catch (e) {
        console.error("Error formateando fecha:", dateString, e);
        return "Fecha inválida";
    }
};

// ===================================================================
// COMPONENTE PRINCIPAL
// ===================================================================

function UserDashboard({ user, onLogout }) {
  // ===== ESTADOS PRINCIPALES =====
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const [iaDisclaimerDone, setIaDisclaimerDone] = useState(() => {
    const normalizedRole = (user?.role || 'user').toLowerCase();
    return localStorage.getItem(`urbania_disclaimer_accepted_${normalizedRole}`) === 'true';
  });
  const [showAnnouncements, setShowAnnouncements] = useState(false);

  const [userInfo, setUserInfo] = useState({
    nombre: user?.name || 'Usuario',
    apartamento: user?.apartment || 'N/A',
    ultimoPago: 'N/A',
    saldoPendiente: '$0.00'
  });

  const [notificaciones, setNotificaciones] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    pagosRealizados: 0,
    pagosPendientes: 0,
    pendientesAprobacion: 0,
    total: 0,
    progreso: 0
  });

  // ===== FUNCIONES AUXILIARES =====
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    closeMobileSidebar();
  };

  const getToken = () => localStorage.getItem('token') || '';

  const recalculateStats = (currentPayments) => {
    const total = currentPayments.length;
    const pagados = currentPayments.filter(p => p.status === 'Paid').length;
    const pendientes = currentPayments.filter(p => p.status === 'Pending').length;
    const pendientesAprobacion = currentPayments.filter(p => p.status === 'PendingApproval').length;
    const progreso = total > 0 ? Math.round(((pagados + pendientesAprobacion) / total) * 100) : 0;

    setStats({
      pagosRealizados: pagados,
      pagosPendientes: pendientes,
      pendientesAprobacion,
      total,
      progreso
    });

    const saldo = currentPayments
      .filter(pago => pago.status === 'Pending')
      .reduce((sum, pago) => sum + parseFloat(pago.amount || 0), 0);

    const ultimoPagoPagado = currentPayments
        .filter(pago => pago.status === 'Paid')
        .sort((a, b) => new Date(b.payment_date || b.updated_at || 0) - new Date(a.payment_date || a.updated_at || 0))
        [0];

    setUserInfo(prevInfo => ({
      ...prevInfo,
      saldoPendiente: formatCurrency(saldo),
      ultimoPago: ultimoPagoPagado ? formatDate(ultimoPagoPagado.payment_date || ultimoPagoPagado.updated_at) : 'Ninguno'
    }));
  };

  // ===== HANDLER DE ÉXITO DE PAGO =====
  const handlePaymentSuccess = (updatedPaymentId) => {
    console.log("UserDashboard: handlePaymentSuccess llamado para paymentId:", updatedPaymentId);
    setPagos(prevPagos => {
        const updatedList = prevPagos.map(pago =>
            pago.id === updatedPaymentId
            ? { ...pago, status: 'PendingApproval', estado: 'Pendiente de validación' }
            : pago
        );
        recalculateStats(updatedList);
        return updatedList;
    });
  };

  // ===== EFFECT PARA CARGAR DATOS =====
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const token = getToken();

      if (!token) {
        setError('No autenticado. Por favor, inicie sesión.');
        setLoading(false);
        return;
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      try {
        const [noticesResponse, paymentsResponse] = await Promise.all([
          fetch(API_ENDPOINTS.notices, { headers }),
          fetch(API_ENDPOINTS.paymentsUser, { headers })
        ]);

        if (noticesResponse.ok) {
          const noticesData = await noticesResponse.json();
          const formattedNotices = (noticesData.notices || []).map(notice => ({
            id: notice.id,
            titulo: notice.title,
            fecha: formatDate(notice.created_at),
            descripcion: notice.description
          }));
          setNotificaciones(formattedNotices);
        } else {
          console.error('Error al obtener avisos:', await noticesResponse.text());
        }

        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          const normalizedPayments = (paymentsData.payments || []).map(payment => {
            let estado = '';
            switch (payment.status) {
              case 'Paid': estado = 'Pagado'; break;
              case 'PendingApproval': estado = 'Pendiente de validación'; break;
              default: estado = 'Pendiente';
            }
            payment.amount = parseFloat(payment.amount) || 0;
            return { ...payment, estado };
          });
          setPagos(normalizedPayments);
          recalculateStats(normalizedPayments);
        } else {
          console.error('Error al obtener pagos:', await paymentsResponse.text());
          setError('Error al cargar tus pagos.');
        }

      } catch (err) {
        console.error('Error general al cargar datos:', err);
        setError('Ocurrió un error al cargar los datos. Verifica tu conexión o contacta soporte.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
        fetchData();
        const intervalId = setInterval(fetchData, 60000);
        return () => clearInterval(intervalId);
    } else {
        setLoading(false);
        setPagos([]);
        setNotificaciones([]);
        setStats({ 
          pagosRealizados: 0, 
          pagosPendientes: 0, 
          pendientesAprobacion: 0, 
          total: 0, 
          progreso: 0 
        });
    }
  }, [user?.id]);

  // ===== RENDERIZADO DE SECCIONES =====
  const renderSection = () => {
    if (loading && pagos.length === 0 && notificaciones.length === 0) {
      return (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
              <Spinner animation="border" role="status">
                  <span className="visually-hidden">Cargando...</span>
              </Spinner>
          </div>
      );
    }

    if (error) {
      return <Alert variant="danger">{error}</Alert>;
    }

    switch(activeSection) {
      case 'pagos':
        return (
          <PagosUsers 
            pagos={pagos}
            stats={stats}
            loading={loading}
            onPaymentSuccess={handlePaymentSuccess}
            user={user}
          />
        );

      case 'avisos':
        return (
          <UserNotices 
            notificaciones={notificaciones} 
            loading={loading} 
            error={error} 
            user={user} 
          />
        );

      case 'facturas':
        return <UserFacturas user={user} />;

      case 'reservas':
        return <UserReservas user={user} />;

      case 'perfil':
        return <UserProfile user={user} onLogout={onLogout} />;

      default: // 'inicio'
        return (
          <UserDashboardInicio
            userInfo={userInfo}
            notificaciones={notificaciones}
            pagos={pagos}
            stats={stats}
            loading={loading}
            setActiveSection={setActiveSection}
          />
        );
    }
  };

  // ===== RENDER PRINCIPAL =====
  return (
    <div className="user-dashboard-container">
      {/* Botón hamburguesa para móvil */}
      <button className="sidebar-mobile-toggle" onClick={toggleMobileSidebar}>
        {mobileSidebarOpen ? <FaTimes /> : <FaBars />}
      </button>

      {/* Overlay para cerrar sidebar en móvil */}
      <div
        className={`sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
        onClick={closeMobileSidebar}
      />

      {/* SIDEBAR con clases responsive */}
      <div className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'open' : ''}`}>
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>

        <div className="sidebar-header">
          <h2>{user?.name || 'Mi Panel'}</h2>
        </div>

        <ul className="sidebar-menu">
          <li
            data-tooltip="Inicio"
            className={activeSection === 'inicio' ? 'active' : ''}
            onClick={() => handleSectionChange('inicio')}
          >
            <FaHome /> <span>Inicio</span>
          </li>

          <li
            data-tooltip="Avisos"
            className={activeSection === 'avisos' ? 'active' : ''}
            onClick={() => handleSectionChange('avisos')}
          >
            <FaBell /> <span>Avisos</span>
            {notificaciones.length > 0 && (
              <span className="badge">{notificaciones.length}</span>
            )}
          </li>

          <li
            data-tooltip="Mis Pagos"
            className={activeSection === 'pagos' ? 'active' : ''}
            onClick={() => handleSectionChange('pagos')}
          >
            <FaFileInvoiceDollar /> <span>Mis Pagos</span>
          </li>

          <li
            data-tooltip="Facturas"
            className={activeSection === 'facturas' ? 'active' : ''}
            onClick={() => handleSectionChange('facturas')}
          >
            <FaFileInvoice /> <span>Facturas</span>
          </li>

          <li
            data-tooltip="Reservas"
            className={activeSection === 'reservas' ? 'active' : ''}
            onClick={() => handleSectionChange('reservas')}
          >
            <FaCalendarAlt /> <span>Reservas</span>
          </li>

          <li
            data-tooltip="Mi Perfil"
            className={activeSection === 'perfil' ? 'active' : ''}
            onClick={() => handleSectionChange('perfil')}
          >
            <FaUser /> <span>Mi Perfil</span>
          </li>
        </ul>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className={`dashboard-main ${sidebarCollapsed ? 'expanded' : ''}`}>
        <div className="dashboard-content">
          {renderSection()}
        </div>
      </div>

      {/* Chat flotante */}
      {user && <ChatBubble user={user} />}

      {/* UrbanIA - Asistente Inteligente */}
      {!iaDisclaimerDone && (
        <UrbanIADisclaimer
          userName={user?.name || 'Residente'}
          role={user?.role || 'User'}
          onAccept={() => {
            setIaDisclaimerDone(true);
            setTimeout(() => setShowAnnouncements(true), 500);
          }}
        />
      )}
      {iaDisclaimerDone && <UrbanIA user={user} />}

      {/* Popup de avisos 3D - se muestra después del disclaimer IA */}
      {showAnnouncements && notificaciones.length > 0 && (
        <AnnouncementPopup
          announcements={notificaciones}
          userId={user?.id}
          onClose={() => setShowAnnouncements(false)}
        />
      )}
    </div>
  );
}

export default UserDashboard;